import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule, Router } from '@angular/router';
import { EnrollmentLandingPageInfo, EnrollmentType, ErrorDto, MasterClientProductViewDto } from '../../models/api-data-contracts';
import { LandingPagesService } from '../../services/landing-pages.service';
import { CommonModule } from '@angular/common';
import { SafeHtmlPipe } from "../../pipes/safe-html.pipe";
import { BillingTypes, FormErrors, ResponseErrors } from '../../common/enums';
import { EnrollmentLandingPathRules, LandingPathOption } from '../../common/enrollment-landing-path.rules';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import { paymentFrequencyName, calculateTotalPrice, calculateTotalPriceWithDiscount, filterProductsActiveOn, remapSelectionForDate, toCalendarDateString } from '../../common/helpers';
import { FooterComponent } from "../../components/footer/footer.component";
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { CancellationRequestDialogComponent } from './cancellation-request-dialog.component';

@Component({
  selector: 'app-client-enrollment',
  imports: [CommonModule, SafeHtmlPipe, MatProgressSpinnerModule, RouterModule, FooterComponent, MatButtonToggleModule],
  host:{
    class:"grow flex flex-col"
  },
  templateUrl: './client-landing.component.html',
  styleUrl: './client-landing.component.scss'
})
export class ClientLandingComponent implements OnInit {

  constructor(
    private route: ActivatedRoute,
    private apiService: LandingPagesService,
    private router: Router,
    private dialog: MatDialog
  ){}

  error: string = null;
  errorProduct: string = null;
  info!: EnrollmentLandingPageInfo;
  productList: { productType: string, items: MasterClientProductViewDto[] }[] = [];
  selectedProduct: { [id: string] : MasterClientProductViewDto } = {};
  discountApplied: number = 0;
  selectedEnrollmentType: EnrollmentType | null = null;
  availableProducts: MasterClientProductViewDto[] = [];

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      const id = params['id'];
      if (id != undefined) {
        this.apiService.getLandingPageInfo(id)
        .subscribe({
          next: response => {
            this.info = response.data;
            if(this.info.paymentFrequencies == null || this.info.paymentFrequencies.length == 0){
              this.info.paymentFrequencies = [{ id: 'default', numberOfIntervals: this.info.defaultPaymentFrequency }];
            } else {
              this.info.paymentFrequencies.push({ id: 'default', numberOfIntervals: this.info.defaultPaymentFrequency });
            }
            this.info.paymentFrequencies = this.info.paymentFrequencies.map(frequency => ({
              ...frequency,
              discount: frequency.discount ?? 0
            }));
            this.info.paymentFrequencies.sort((a, b) => a.numberOfIntervals - b.numberOfIntervals);
            this.initializeEnrollmentPath();
          },
          error: (response) => {
            let error = response?.error?.messages?.[0] as ErrorDto;
            if(error?.errorType == ResponseErrors.UserAccess){
              this.error = FormErrors.EnrollmentExpired;
            }
            else
              this.error = FormErrors.GenericError;
          }
        });
      }
    });
  }

  get showPathTabs(): boolean {
    return this.pathTabs.length > 1;
  }

  get pathTabs(): LandingPathOption[] {
    return EnrollmentLandingPathRules.availablePathOptions(this.info, this.info?.products);
  }

  private initializeEnrollmentPath(): void {
    const fromQuery = EnrollmentLandingPathRules.parseEnrollmentTypeQuery(
      this.route.snapshot.queryParamMap.get('enrollmentType')
    );
    const allowed = this.pathTabs.some(p => p.type === fromQuery);
    if (fromQuery && allowed) {
      this.selectEnrollmentType(fromQuery, false);
      return;
    }

    if (this.pathTabs.some(p => p.type === 'OE')) {
      this.selectEnrollmentType('OE', false);
      return;
    }

    if (this.pathTabs.length === 1) {
      this.selectEnrollmentType(this.pathTabs[0].type, false);
      return;
    }

    this.selectedEnrollmentType = null;
    this.availableProducts = [];
    this.productList = [];
    this.selectedProduct = {};
  }

  onSelectPath(type: EnrollmentType): void {
    this.selectEnrollmentType(type, true);
  }

  private selectEnrollmentType(type: EnrollmentType, updateQuery: boolean): void {
    const pathChanged = this.selectedEnrollmentType !== type;
    this.selectedEnrollmentType = type;
    this.errorProduct = null;

    if (updateQuery && this.showPathTabs && this.info?.id) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { enrollmentType: type },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    }

    if (pathChanged || this.availableProducts.length === 0) {
      this.applyPathProducts();
    }
  }

  private applyPathProducts(): void {
    if (!this.selectedEnrollmentType) return;

    const previous = { ...this.selectedProduct };
    const iso = EnrollmentLandingPathRules.pathEffectiveDateIso(this.info, this.selectedEnrollmentType);
    const asOf = iso ?? new Date();
    const catalog = this.info.products ?? [];
    this.availableProducts = filterProductsActiveOn(catalog, asOf);
    this.productList = this.groupByProductTypeArray(this.availableProducts);

    const remapped = remapSelectionForDate(
      catalog,
      Object.values(previous).filter((p): p is MasterClientProductViewDto => !!p),
      asOf,
    );
    this.selectedProduct = {};
    for (const product of remapped) {
      this.selectedProduct[this.pickerKey(product)] = product;
    }

    if (this.availableProducts.length === 1 && !this.hasProductSelection()) {
      const product = this.availableProducts[0];
      this.selectedProduct[this.pickerKey(product)] = product;
    }
  }

  private pickerKey(product: MasterClientProductViewDto): string {
    return product.productType?.toLowerCase().includes('idp') ? 'idp' : 'legal';
  }

  private hasProductSelection(): boolean {
    return Object.values(this.selectedProduct).some(p => !!p);
  }

  planType(type: string){
    if(type.toLowerCase().includes('legal'))
      return `Legal Plans`
    else if(type.toLowerCase().includes('idp'))
      return `Identity Protection Plans`
    else
      return `${type} Plans`;
  }

  planName(name: string){
    if(name.toLowerCase().includes('plan'))
      return name;
    else
      return `${name} Plan`
  }

  get isDirectBilled(): boolean {
    return this.info?.billingType === BillingTypes.DirectBilled;
  }

  billingType(){
    return this.info.billingType == BillingTypes.PayrollDeducted ? 'payroll deduction' : 'direct bill'
  }

  paymentFrequency(){
    return paymentFrequencyName(this.info.defaultPaymentFrequency ?? 1)
  }

  intervalName(numberOfIntervals: number | null): string {
    return paymentFrequencyName(numberOfIntervals ?? 1);
  }

  planDisplayRate(plan: MasterClientProductViewDto): number {
    return (plan.memberContribution ?? 0) * (this.info.defaultPaymentFrequency ?? 1);
  }

  getPrice() {
    return calculateTotalPrice(this.selectedProduct, this.info.defaultPaymentFrequency);
  }

  getPriceWithDiscount() {
    const discount = this.info.paymentFrequencies.find(f => f.numberOfIntervals === this.info.defaultPaymentFrequency)?.discount || 0;
    this.discountApplied = discount;
    return calculateTotalPriceWithDiscount(this.selectedProduct, this.info.defaultPaymentFrequency, discount);
  }

  groupByProductTypeArray(products: MasterClientProductViewDto[]): { productType: string, items: MasterClientProductViewDto[] }[] {
    const map = new Map<string, MasterClientProductViewDto[]>();

    for (const product of products) {
      const type = product.productType;
      if (!type) continue;
      if (!map.has(type)) {
        map.set(type, []);
      }
      map.get(type)!.push(product);
    }

    return Array.from(map.entries()).map(([productType, items]) => ({
      productType,
      items
    }));
  }

  toggleProduct(type: string, plan: MasterClientProductViewDto) {
    if (this.selectedProduct[type]?.clientProductId === plan.clientProductId) {
      this.selectedProduct[type] = null;
    } else {
      this.selectedProduct[type] = plan;
    }
  }

  goToEnrollment(): void {
    if (!this.selectedEnrollmentType) {
      this.errorProduct = 'Please choose Open Enrollment or New Hire to continue.';
      return;
    }

    const ids = Object.values(this.selectedProduct)
      .filter((p): p is MasterClientProductViewDto => !!p?.clientProductId)
      .map(p => p.clientProductId!);

    if (ids.length > 0) {
      this.errorProduct = null;
      this.router.navigate(['/enroll', this.info.id], {
        queryParams: {
          selected: ids,
          paymentFrequency: this.info.defaultPaymentFrequency,
          enrollmentType: this.selectedEnrollmentType,
        },
      });
    } else {
      this.errorProduct = 'Please select at least one product to continue.';
    }
  }

  get selectedPath(): LandingPathOption | null {
    if (!this.selectedEnrollmentType) return null;
    return this.pathTabs.find(p => p.type === this.selectedEnrollmentType) ?? null;
  }

  get unavailableEnrollmentLabel(): string {
    return this.getEnrollmentButtonLabel(this.info);
  }

  get showFutureOpenEnrollmentNotice(): boolean {
    if (!this.info || this.info.alwaysOpenEnrollment === true) return false;
    return (this.info.status ?? '').toString().toLowerCase() === 'future';
  }

  get futureOpenEnrollmentStart(): string | null {
    if (!this.showFutureOpenEnrollmentNotice) return null;
    return this.monthDayYear(this.info.startDate);
  }

  get showStickyEnrollmentBar(): boolean {
    return this.getPriceWithDiscount() > 0 || this.showFutureOpenEnrollmentNotice;
  }

  get showUnavailableEnrollmentMessage(): boolean {
    return this.pathTabs.length === 0 && !this.showFutureOpenEnrollmentNotice;
  }

  get showCancellationRequestLink(): boolean {
    return EnrollmentLandingPathRules.canRequestOpenEnrollmentCancellation(this.info);
  }

  openCancellationRequest(): void {
    if (!this.showCancellationRequestLink || !this.info?.id) {
      return;
    }

    this.dialog.open(CancellationRequestDialogComponent, {
      width: '28rem',
      maxWidth: '95vw',
      disableClose: false,
      data: { landingPageId: this.info.id },
    });
  }

  getEnrollmentButtonLabel(page: EnrollmentLandingPageInfo): string {
    switch ((page.status ?? '').toString().toLowerCase()) {
      case 'unavailable':
        return 'Company is not Open Enrollment'
      case 'future': {
        const start = this.monthDayYear(page.startDate);
        return start ? `Open Enrollment Starts: ${start}` : 'Open Enrollment has not started yet';
      }
      case 'expired':
        return 'Enrollment Expired';
      default:
        return 'Continue to Enrollment';
    }
  }

  /** MM/DD/YYYY from the existing YYYY-MM-DD calendar string. */
  private monthDayYear(value: string | null | undefined): string | null {
    const iso = toCalendarDateString(value);
    if (!iso) return null;
    const [year, month, day] = iso.split('-');
    return `${month}/${day}/${year}`;
  }
}
